import * as React from 'react';
import {
    IndividualReportBlock,
    PVAPage,
    PVAActionsButtonGroup,
    LoadingIndicator
} from '../../../components';
import { FoundationsReport } from './FoundationsReport';
import './MyReport.scss';
import { UserPermissions, Style } from '../../../models';
import { hasPermission } from '../../../utilities/User';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import { ReportDataService, Auth } from '../../../services';
import { exportToPDF } from '../../../utilities/Pdf';
import Messages from '../../../utilities/Messages';
import { ReportType, MidRangeType } from '../../../enums';
import { BaseRouteProps } from '../../../interfaces';
import * as moment from 'moment';

interface MyReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    Auth?: Auth;
}

interface MyReportState {
    loading: boolean;
    error: boolean;
    reportType: ReportType;
    midRangeType: MidRangeType;
    small: boolean;
    showNotes: boolean;
    pdfLoader: boolean;
    fetchedOrgId: number;
    fetchedSurveyId: number;
    fetchedSurveyVersionId: number;
}

@inject('UserStore', 'FilterStore', 'Auth') @observer
export class MyReport extends React.Component<MyReportProps, MyReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);

    constructor(props: MyReportProps) {
        super(props);

        this.state = {
            loading: false,
            error: false,
            reportType: ReportType.Standard,
            midRangeType: MidRangeType.Default,
            small: false,
            showNotes: false,
            pdfLoader: false,
            fetchedOrgId: 0,
            fetchedSurveyId: 0,
            fetchedSurveyVersionId: 0
        };
    }

    @computed get hasFoundationsReport() {
        if (this.selectedUserData && this.selectedUserData.userPermissions) {
            return hasPermission(this.selectedUserData.userPermissions, UserPermissions.Foundations);
        }
        return hasPermission(this.props.UserStore!.userInfo.userPermissions, UserPermissions.Foundations);
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users;
        }
        return [];
    }

    @computed get selectedUserData() {
        return this.props.FilterStore!.individualReportUserData;
    }

    @computed get userId() {
        if (this.props.FilterStore && this.props.FilterStore.individualReportUserId) {
            return this.props.FilterStore.individualReportUserId;
        }
        return this.props.UserStore!.userInfo.id;
    }

    private update() {
        if (this.state.loading) return;

        const orgId = (this.props.FilterStore && this.props.FilterStore.organization) ? this.props.FilterStore.organization.id : 0;
        const surveyId = (this.props.FilterStore && this.props.FilterStore.survey) ? this.props.FilterStore.survey.id : 0;
        const surveyVersionId = (this.props.FilterStore && this.props.FilterStore.surveyVersion) ? this.props.FilterStore.surveyVersion.id : 0;

        const filtersChanged = (orgId !== 0 && orgId !== this.state.fetchedOrgId) ||
                               (surveyId !== 0 && surveyId !== this.state.fetchedSurveyId) ||
                               (surveyVersionId !== 0 && surveyVersionId !== this.state.fetchedSurveyVersionId);

        if (this.props.FilterStore!.individualReportUserId !== this.props.UserStore!.userInfo.id) {
            this.props.FilterStore!.individualReportUserId = this.props.UserStore!.userInfo.id;
            this.setState({
                fetchedOrgId: orgId,
                fetchedSurveyId: surveyId,
                fetchedSurveyVersionId: surveyVersionId
            });
            this.userChange({ target: { value: this.props.UserStore!.userInfo.id } });
        } else if (filtersChanged) {
            this.setState({
                fetchedOrgId: orgId,
                fetchedSurveyId: surveyId,
                fetchedSurveyVersionId: surveyVersionId
            });
            this.userChange({ target: { value: this.props.UserStore!.userInfo.id } });
        } else if (!this.selectedUserData && !this.state.loading && !this.state.error) {
            this.userChange({ target: { value: this.props.UserStore!.userInfo.id } });
        }
    }

    componentDidMount() {
        Messages.listen(IndividualReportBlock.SupportedMessages.returnBriefReport, (briefReport) => {
            exportToPDF(briefReport, `Brief_Report_${this.selectedUserData.name}.pdf`);
            this.setState({ pdfLoader: false });
        });
        
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public userChange(e: any) {
        const userId: number = Number(e.target.value);

        if (this.props.FilterStore!.survey && userId) {
            this.setState({ loading: true, error: false });
            this.props.FilterStore!.individualReportUserId = userId;
            this.props.FilterStore!.individualReportUserData = undefined;
            let surveyVersionID: number | undefined = undefined;
            if (this.props.FilterStore!.surveyVersion && !this.props.FilterStore!.surveyVersion!.published) {
                surveyVersionID = this.props.FilterStore!.surveyVersion!.id;
            }

            this.reportDataService.getIndividualReportData(this.props.FilterStore!.survey!.id, userId, surveyVersionID).then((reportData: any) => {
                const loggedInUserId = this.props.UserStore!.userInfo.id;
                const reportUserId = reportData && (reportData.userId || reportData.userID);

                if (reportUserId !== loggedInUserId) {
                    this.reportDataService.getIndividualReportData(this.props.FilterStore!.survey!.id, loggedInUserId, surveyVersionID).then((loggedInUserReportData: any) => {
                        this.props.FilterStore!.individualReportUserId = loggedInUserId;
                        this.props.FilterStore!.individualReportUserData = loggedInUserReportData;
                        this.setState({ loading: false });
                    }).catch(() => {
                        this.setState({ loading: false, error: true });
                    });
                    return;
                }

                this.props.FilterStore!.individualReportUserData = reportData;
                this.setState({ loading: false });
            }).catch(() => {
                // Handle error if needed, but at least don't crash
                this.setState({ loading: false, error: true });
            });
        } else {
            this.props.FilterStore!.individualReportUserId = userId;
            this.props.FilterStore!.individualReportUserData = undefined;
            this.setState({ loading: false, error: false });
        }
    }

    public instanceChange(e: any) {
        const instanceId: number = Number(e.target.value);
        if (instanceId) {
            this.setState({ loading: true, error: false });
            this.reportDataService.getIndividualReportDataByInstance(instanceId).then((reportData: any) => {
                this.props.FilterStore!.individualReportUserData = reportData;
                this.setState({ loading: false });
            }).catch(() => {
                this.setState({ loading: false, error: true });
            });
        }
    }

    private HasAccessToMidrange() {
        return this.HasAccessToViewMidranges() && this.props.UserStore!.checkUserPermission(UserPermissions.CanViewMidRangeChoices);
    }
    private HasAccessToViewMidranges() {
        return (this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewMidRanges));
    }

    private PdfExportEnabled() {
        return this.hasFoundationsReport || !!this.props.FilterStore!.individualReportUserId;
    }

    private exportPDF = () => {
        if (this.PdfExportEnabled()) {
            this.setState({ pdfLoader: true });
            this.reportDataService.getIndividualReportDataPDF(this.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then(() => {
                this.setState({ pdfLoader: false });
            }).catch((error) => {
                console.log(error);
                this.setState({ pdfLoader: false });
            });
        }
    }

    private exportBriefPDF = () => {
        if (this.PdfExportEnabled()) {
            this.setState({ pdfLoader: true });
            this.reportDataService.getIndividualBriefPDF(this.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then(() => {
                this.setState({ pdfLoader: false });
            }).catch((error) => {
                console.log(error);
                this.setState({ pdfLoader: false });
            });
        }
    }

    // private exportBriefPDF = () => {
    //     if (this.PdfExportEnabled()) {
    //         if (this.state.pdfLoader) return;
    //         this.setState({ pdfLoader: true });
    //         Messages.send(IndividualReportBlock.SupportedMessages.createBriefReport, null);
    //     }
    // }

    private retryLoad = () => {
        if (this.selectedUserData && this.selectedUserData.surveyInstanceId) {
            this.instanceChange({ target: { value: this.selectedUserData.surveyInstanceId } });
        } else if (this.props.FilterStore!.individualReportUserId) {
            this.userChange({ target: { value: this.props.FilterStore!.individualReportUserId } });
        } else {
            this.update();
        }
    }

    public render() {
        if (this.hasFoundationsReport) {
            return <FoundationsReport {...this.props} selectedUserData={this.selectedUserData} hideDichotomies={false} />;
        }

        const actionGroups: PVAActionsButtonGroup[] = [];

        if (this.state.reportType === ReportType.Midrange && this.HasAccessToMidrange()) {
            const items = [
                {
                    id: 'midrange-pva',
                    name: 'Mid Range: PVA',
                    callback: () => this.setState({ midRangeType: MidRangeType.Default })
                },
                {
                    id: 'midrange-team',
                    name: 'Mid Range: Current Team',
                    callback: () => this.setState({ midRangeType: MidRangeType.Team })
                },
                {
                    id: 'midrange-organization',
                    name: 'Mid Range: Current Organization',
                    callback: () => this.setState({ midRangeType: MidRangeType.Organization })
                }
            ];
            if (this.props.UserStore!.userRoleIn([Roles.SuperAdmin])) {
                items.push({
                    id: 'midrange-all',
                    name: 'Mid Range: All PVA Data',
                    callback: () => this.setState({ midRangeType: MidRangeType.All })
                });
            }
            actionGroups.push({ header: 'Mid Range Options', items });
        }

        if (this.HasAccessToViewMidranges()) {
            const items = [];
            if (this.state.reportType !== ReportType.Standard || this.state.showNotes) {
                items.push({
                    id: 'standard-view',
                    name: 'Standard View',
                    icon: 'standardIcon.svg',
                    description: 'View standard individual report',
                    callback: () => this.setState({ reportType: ReportType.Standard, showNotes: false })
                });
            }
            if (this.state.reportType !== ReportType.Midrange || this.state.showNotes) {
                items.push({
                    id: 'midrange-view',
                    name: 'Mid Range View',
                    icon: 'midrangeIcon.svg',
                    description: 'View the individual report with your scores as they relate to the mid-range of 1.25 standard deviation below and above the mean for each scale',
                    callback: () => this.setState({ reportType: ReportType.Midrange, showNotes: false })
                });
            }
            actionGroups.push({ header: 'Change View', items });
        }

        // const sizeItems = [];
        // if (!this.hasFoundationsReport) {
        //     if (!this.state.small) {
        //         // sizeItems.push({
        //         //     id: 'view-small',
        //         //     name: 'View Small Report',
        //         //     icon: 'Icon awesome-search.svg',
        //         //     description: 'Show the report as a smaller version to better fit small screens',
        //         //     callback: () => this.setState({ small: true })
        //         // });
        //     } else {
        //         sizeItems.push({
        //             id: 'view-normal',
        //             name: 'View Normal Report',
        //             icon: 'Icon awesome-search.svg',
        //             description: 'Show the report at its normal size',
        //             callback: () => this.setState({ small: false })
        //         });
        //     }
        //     actionGroups.push({ header: 'Report Size', items: sizeItems });
        // }

        const exportItems = [];
        const pdfExportEnabled = this.PdfExportEnabled();
        if (pdfExportEnabled) {
            exportItems.push({
                id: 'export-brief-pdf',
                name: 'Export PDF Brief Report',
                icon: 'Icon awesome-file-pdf.svg',
                description: 'PDF Export',
                callback: () => this.exportBriefPDF()
            });
            exportItems.push({
                id: 'export-full-pdf',
                name: 'Export Full-Detail PDF Report',
                icon: 'Icon awesome-file-pdf.svg',
                description: 'PDF Export',
                callback: () => this.exportPDF()
            });
        }
        if (exportItems.length > 0) {
            actionGroups.push({ header: 'Export Options', items: exportItems });
        }

        if (this.selectedUserData && this.selectedUserData.others && this.selectedUserData.others.length > 0) {
            const items = this.selectedUserData.others.map((otherData: any) => {
                return {
                    id: `other-${otherData.id}`,
                    name: `${moment(otherData.dateTime).format('MM/DD/YYYY')} (${otherData.id})`,
                    icon: 'glyphicon-save-file',
                    dateTime: otherData.dateTime,
                    callback: () => this.instanceChange({ target: { value: otherData.id } })
                };
            }).sort((a: any, b: any) => {
                if (moment(a.dateTime).isBefore(b.dateTime)) {
                    return 1;
                } else if (moment(a.dateTime).isAfter(b.dateTime)) {
                    return -1;
                }
                return 0;
            });
            actionGroups.push({ header: 'Past Surveys', items });
        }

        if ((this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAssessmentNotes)) && !this.state.showNotes) {
            actionGroups.push({
                header: 'Other',
                items: [{
                    id: 'notes',
                    name: 'Notes',
                    icon: 'glyphicon-edit',
                    callback: () => this.setState({ showNotes: true })
                }]
            });
        }

        return <PVAPage>
            <PVAPage.Header actions={actionGroups}>
                <PVAPage.Header.Title>
                    <PVAPage.Header.Title.Text>
                        <span className="greyDark">My PVA Report<span className="trademark">TM</span> for: </span> {this.selectedUserData ? this.selectedUserData.name : ''}
                    </PVAPage.Header.Title.Text>
                </PVAPage.Header.Title>
            </PVAPage.Header>
            <PVAPage.Body>
                {this.state.pdfLoader && <LoadingIndicator showPdfDescription={true} showInPanel={true} />}
                {this.state.error && (
                    <div className="alert alert-danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Error retrieving individual report.</span>
                        <button className="btn btn-primary btn-sm" onClick={this.retryLoad}>Retry</button>
                    </div>
                )}
                {!this.state.error && (
                    <IndividualReportBlock 
                        messages="myreport"
                        loading={this.state.loading} 
                        userId={this.props.FilterStore!.individualReportUserId} 
                        history={this.props.history} 
                        match={this.props.match} 
                        location={this.props.location} 
                        notificationService={this.props.notificationService} 
                        UserStore={this.props.UserStore} 
                        reportType={this.state.reportType}
                        midRangeType={this.state.midRangeType}
                        small={this.state.small}
                        showNotes={this.state.showNotes}
                        onNotesChange={(show) => this.setState({ showNotes: show })}
                        selectedUserData={this.selectedUserData}
                        survey={this.props.FilterStore!.survey}
                        surveyVersion={this.props.FilterStore!.surveyVersion}
                        noSurveys={this.props.FilterStore!.noSurveys}
                        organization={this.props.FilterStore!.organization}
                        surveyReportType={this.props.FilterStore!.surveyReportType}
                        users={this.props.FilterStore!.users}
                        teamId={this.props.FilterStore!.teamId}
                    />
                )}
            </PVAPage.Body>
            {!this.state.loading && <PVAPage.Footer />}
        </PVAPage>;
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/myreport/MyReport.tsx